import { FetchResult } from '@apollo/client';

import { ENDS, ETaxVariant } from 'cashbox';
import {
  IAutorunApplication,
  EPaymentItemType,
  Tax,
  ITelegramChannelId,
} from 'types';

export enum EBlacklistType {
  'APPLICATION' = 'APPLICATION',
  WINDOW = 'WINDOW',
  TRAY = 'TRAY',
}

export interface ITax {
  entities: Array<EPaymentItemType> | null;
  taxVariant: ETaxVariant | null;
  nds: ENDS | null;
}

export type TBlacklistItem = {
  type: EBlacklistType;
  name: string;
  class: string;
};

export type TTelegramNotificationsTypes =
  | 'workshifts'
  | 'user_deposit'
  | 'user_discount'
  | 'refund'
  | 'bonus'
  | 'shell_status'
  | 'cash_order'
  | 'review'
  | 'security'
  | 'warehouse'
  | 'worker_auth'
  | 'low_stock'
  | 'loyalty_program'
  | 'advertisement';

export type TTelegramNotificationsConfig = Record<
  TTelegramNotificationsTypes,
  '1' | '0'
>;

export enum ESettingsAliases {
  CLUB_NAME = 'shell.club_name',
  CLIENT_DISCOUNT = 'backend.discount.use_personal_as_global',
  PRINT_REPORT = 'billing.request_report_print',
  PRINT_REPORT_ALL_GOODS = 'backend.report_print.all_goods.enabled',
  REFUNDS = 'backend.refunds.enabled',
  REFUNDS_HEADROOM = 'backend.refunds.headroom',
  SHELL_WHITE_LIST_ENABLED = 'shell.shield.system.enable_allow_run',
  SHELL_WHITE_LIST = 'shell.shield.system.allow_run',
  SHELL_PASSWORD = 'shell.fastoff.fastoff_fixed_pwd_md5',
  SHELL_END_SESSION_ACTION = 'shell.end_session_action.action',
  SHELL_END_SESSION_TIMEOUT = 'shell.end_session_action.timeout',
  SHELL_AUTORUN = 'shell.autorun',
  SHELL_VISIBLE_DISCS = 'shell.visible_discs',
  SHELL_RESTRICT_CHROME_DOWNLOAD = 'shell.restrict_chrome_download',
  SHELL_GOODS_DISPLAY_ENABLED = 'shell.goods.display.enabled',
  SENET_USER_IMPORT = 'backend.import.senet.id',
  SHELL_PER_MINUTE_ON_LOGIN = 'shell.run_per_minute_on_login',
  SHELL_PER_MINUTE_ON_SESSION_END = 'shell.run_per_minute_on_session_end',
  SHELL_SELF_HOST_CHANGE = 'shell.self_host_change',
  SHELL_HOST_GROUP_SWITCH = 'shell.client_session.host_group_switch.enabled',
  SHELL_HOST_GROUP_SWITCH_PAUSABLE = 'shell.client_session.host_group_switch.pausable.enabled',
  DEFENCE_BLACKLIST = 'shell.blacklist',
  TELEGRAM_CHANNEL_ID = 'backend.telegram.channel_id',
  TELEGRAM_TOKEN = 'backend.telegram.token',
  TELEGRAM_NOTIFICATION_TYPES = 'backend.telegram.notifications',
  CLUB_RULES_ENABLED = 'shell.rules.enabled',
  CLUB_RULES_TEXT = 'shell.rules.text',
  EXTERNAL_STORAGES = 'shell.removable_storage',
  CURRENCY = 'backend.currency',
  CLIENT_RATING_ENABLED = 'backend.leaderboard.enabled',
  CLIENT_RATING_SHELL_ENABLED = 'shell.leaderboard.enabled',
  LOW_STOCK_ENABLED = 'backend.goods.low_stock.enabled',
  LOW_STOCK_THRESHOLD = 'backend.goods.low_stock.threshold',
  CLIENT_REGISTER = 'backend.worker.client_register.enabled',
  REGISTER_WITHOUT_VERIFICATION = 'shell.register_without_verification.enabled',
  FASTOFF_SMART_LOCK_PWD = 'shell.fastoff.smart_lock_pwd',
  HOST_MAP = 'backend.host_map.layout',

  /** CASHBOX */
  CASHBOX = 'cashbox.enabled',
  CASHBOX_SELECTED_INN = 'cashbox.selected_inn',
  DEPOSIT_NDS = 'cashbox.tax.type.deposit',
  GOODS_NDS = 'cashbox.tax.type.goods',
  SERVICES_NDS = 'cashbox.tax.type.services',
  TARIFFS_NDS = 'cashbox.tax.type.tariffs',
  DEPOSIT_TAX_SYSTEM = 'cashbox.tax.system.deposit',
  GOODS_TAX_SYSTEM = 'cashbox.tax.system.goods',
  SERVICES_TAX_SYSTEM = 'cashbox.tax.system.services',
  TARIFFS_TAX_SYSTEM = 'cashbox.tax.system.tariffs',
  CASHIER_NAME = 'cashbox.cashier.name',
  DEPOSIT_REFILL_TITLE = 'cashbox.deposit_refill.title',

  /** STRICT BOOKING */
  BOOKING_STRICT_MODE = 'shell.booking_strict_mode.on',
  BOOKING_STRICT_MODE_HEADROOM = 'shell.booking_strict_mode.headroom',
  SHOW_SEANS_ON_MAP = 'backend.show_seances_on_map.enabled',
  BOOKING_EXPIRATION_THRESHOLD = 'backend.booking.expiration_threshold',

  /** SHELL_IDLE */
  SHELL_SCREENSAVER = 'shell.skin.idle.image',
  SHELL_SCREENSAVER_TIMEOUT = 'shell.skin.idle.timeout',
  SHELL_SMART_KIOSK = 'backend.skin.smart_kiosk.image',
  SHELL_SMART_TV = 'shell.skin.smart_tv.image',

  /** FEEDBACK */
  FEEDBACK_ENABLED = 'shell.feedback.enabled',

  /** CALL_ADMIN */
  CALL_ADMIN = 'shell.call_admin.enabled',
  CALL_ADMIN_TIMEOUT = 'backend.call_admin.timeout',

  /** AUTO_TURNOFF */
  AUTO_TURNOFF_ENABLED = 'shell.auto_turnoff.enabled',
  AUTO_TURNOFF_TIMEOUT = 'shell.auto_turnoff.timeout',

  /** HIDDEN SETTINGS */
  REMOTE_CONTROL_CCBOOT = 'shell.remote_control_ccboot.enabled',
  REMOTE_CONTROL_ENABLED = 'shell.remote_control.enabled',
  REMOTE_CONTROL_PASSWORD = 'shell.remote_control.password',
  RESTRICT_VIRTUAL_HOST_DEPOSIT_PAYMENT = 'backend.restrict_virtual_host_deposit_payment.enabled',

  /** SHELL */
  SHELL_LANGUAGE = 'shell.lang',
  MOUSE_UTIL = 'shell.mouse_util.enabled',
  VIDEO_UTIL = 'shell.video_util.enabled',
  SOUND_UTIL = 'shell.sound_util.enabled',

  /** ONLINE BOOKING */
  ONLINE_BOOKING = 'backend.booking.online.enabled',
  ONLINE_BOOKING_CANCEL = 'backend.booking.self_cancel.enabled',
  ONLINE_BOOKING_CANCEL_UNTIL = 'backend.booking.self_cancel.free_headroom',
  ONLINE_BOOKING_PENALTY = 'backend.booking.self_cancel.penalty',
  ONLINE_BOOKING_BUSY = 'backend.public_client_sessions.enabled',
  ONLINE_BOOKING_MULTIPLE_HOST = 'backend.booking.online.multiple_hosts.enabled',
  ONLINE_BOOKING_MIN_DURATION = 'backend.booking.online.min_duration',
  ONLINE_BOOKING_MAX_DURATION = 'backend.booking.online.max_duration',
  ONLINE_BOOKING_PER_MINUTE_ENDING = 'backend.booking.online.per_minute_ending.enabled',
  ONLINE_BOOKING_DISCOUNT = 'backend.booking.online.discount',
  ONLINE_BOOKING_BLOCK_DURATION = 'backend.session.booking.block_duration',
  ONLINE_PAYMENTS_MIN_LIMIT = 'shell.online_payment.min_limit',

  /** CLOUD_PAYMENTS */
  CLOUD_PAYMENTS = 'backend.cloudpayments.enabled',
  CLOUD_PAYMENTS_PUBLIC_ID = 'backend.cloudpayments.public_id',
  CLOUD_PAYMENTS_VAT = 'backend.online_payment.tax.type',

  /** COUNTRY */
  TIMEZONE = 'backend.timezone',
  COUNTRY_CODE = 'backend.country_code',

  /** HOLIDAY TARIFFS */
  HOLIDAY_TARIFFS_ENABLED = 'backend.holiday_tariffs.enabled',
  HOLIDAY_TARIFFS_DATES = 'backend.holiday_tariffs.dates',

  /** ПОСТОПЛАТА */
  POST_PAYMENT_ENABLED = 'backend.post_payment.enabled',
  POST_PAYMENT_MAX_DURATION = 'backend.post_payment.max_duration',

  /** TINKOFF */
  TINKOFF_SBP_ENABLED = 'shell.tinkoff.sbp.enabled',

  /** BONUS SYSTEM */
  BONUS_GOODS_ENABLED = 'shell.bonus.goods.enabled',
  BONUS_SERVICES_ENABLED = 'shell.bonus.services.enabled',
  BONUS_SYSTEM_ENABLED = 'shell.bonus_system.enabled',
  DEPOSIT_REFILL_ENABLED = 'shell.bonus_system.deposit_refill_enabled',
  TARIFFS_ENABLED = 'shell.bonus.tariffs.enabled',
  USE_DEPOSIT_FIRST_ENABLED = 'shell.bonus.use_deposit_first.enabled',
  USAGE_PERCENT = 'shell.bonus.usage_percent',

  /** KASPI */
  KASPI_ONLINE_ENABLED = 'shell.kaspi.online.enabled',
  KASPI_ONLINE_BIN = 'backend.kaspi.online.bin',

  /** STRIPE */
  STRIPE_ENABLED = 'shell.stripe.enabled',
  STRIPE_SECRET_KEY = 'backend.stripe.secret_key',
  STRIPE_PUBLISHABLE_KEY = 'backend.stripe.publishable_key',

  /** BEPAID */
  BE_PAID_ENABLED = 'backend.bepaid.enabled',
  BE_PAID_SECRET_KEY = 'backend.bepaid.public_key',
  BE_PAID_PUBLISHABLE_KEY = 'backend.bepaid.secret_key',
  BE_PAID_SHOP_ID = 'backend.bepaid.shop_id',

  /** ЧАЕВЫЕ */
  TIPS_ENABLED = 'shell.tips.enabled',
  TIPS_VALUES = 'shell.tips.values',

  /** CUSTOMIZATION SHELL */
  SHELL_SKIN_ACCENT_COLOR = 'shell.skin.accent_color',
  SHELL_BG_SKIN = 'shell.skin.desktop.image',
  SHELL_SKIN_ADDITIONAL_COLOR = 'shell.skin.additional_color',
  SHELL_SKIN_BACKGROUND_COLOR = 'shell.skin.background_color',
  SHELL_SKIN_LOGO = 'shell.skin.logo',

  /** AUTOSTART SESSION */

  SHELL_FAST_LOGIN_AUTOSTART_SESSION_ENABLED = 'shell.fast_login.autostart_session.enabled',
  SHELL_FAST_LOGIN_AUTOSTART_SESSION_TARIFF_ID = 'shell.fast_login.autostart_session.tariff_id',

  HARDWARE_CHANGED = 'backend.hardware_changes.disabled',
  LITE_SHELL = 'shell.lite_shell.enabled',
}

export interface ICurrency {
  id: number;
  alias: string;
  title: string;
  letter: string;
}

export type TSetSetting = ({
  alias,
  value,
}: {
  alias: ESettingsAliases;
  value: string | null;
}) => Promise<FetchResult<ISetSettingResponse>>;

export type TSetSettings = (
  settings: Array<{
    alias: ESettingsAliases;
    value: string | null;
  }>
) => Promise<FetchResult<ISetSettingsResponse>>;

export interface ISettingsContext {
  state: {
    currencies: Array<ICurrency>;
    settings: {
      autorun: Array<IAutorunApplication>;
      clubName: string;
      isWhiteListEnabled: boolean;
      whiteList: Array<string>;
      action: number;
      timeout: number | '';
      lastUploadedFileId: number;
      visibleDiscs: string;
      shouldUseClientDiscount: boolean;
      shouldPrintReport: boolean;
      printReportAllGoods: boolean;
      runPerMinuteOnLogin: boolean;
      runPerMinuteOnSessionEnd: boolean;
      isChromeDownloadRestrict: boolean;
      selfHostChange: boolean;
      hostGroupSwitch: boolean;
      hostGroupSwitchPausable: boolean;
      isRefundEnabled: boolean;
      refundHeadroom: number;
      blacklist: Array<TBlacklistItem>;
      telegramChannelId: ITelegramChannelId | null;
      telegramToken: string;
      telegramNotificationTypes: TTelegramNotificationsConfig;
      isClubRulesEnabled: boolean;
      clubRules: string;
      externalStorages: boolean;
      isCashboxEnabled: boolean;
      selectedCashboxINN: string | null;
      taxes: Array<ITax>;
      shellBgSkin: string | null;
      screenSaver: string | null;
      screenSmartKiosk: string | null;
      screenSmartTV: string | null;
      screenSaverTimeout: number;
      callAdmin: boolean;
      callAdminTimeout: number;
      autoTurnoffEnabled: boolean;
      shellGoodsDisplayEnabled: boolean;
      autoTurnoffTimeout: number;
      currency: {
        value: string;
        sign: string;
      };
      feedbackEnabled: boolean;
      bookingStrictMode: boolean;
      bookingStrictModeHeadroom: number;
      bookingExpirationThreshold: number;
      remoteControlCCBoot: boolean;
      remoteControlEnabled: boolean;
      remoteControlPassword: string;
      removeControlPasswordUpdated: string | null;
      restrictVirtualHostDepositPayment: boolean;
      shellLanguage: 'RU' | 'EN';
      mouseUtil: boolean;
      videoUtil: boolean;
      soundUtil: boolean;
      onlineBooking: boolean;
      onlineBookingDurationMin: number;
      onlineBookingDurationMax: number;
      onlineBookingCancel: boolean;
      onlineBookingCancelUntil: number;
      onlineBookingPenalty: number;
      onlineBookingBusy: boolean;
      onlineBookingMultipleHost: boolean;
      timezone: number;
      cloudPayments: boolean;
      publicId: null | string;
      holidayTariffsEnabled: boolean;
      holidayTariffsDates: string[];
      tinkoffSbpEnabled: boolean;
      showSeans: boolean;
      postPaymentEnabled: boolean;
      postPaymentMaxDuration: number;
      onlineBookingPerMinuteEndingEnabled: boolean;
      clientRatingEnabled: boolean;
      clientRatingShellEnabled: boolean;
      onlineBookingDiscount: number;
      bookingBlockDuration: number;
      onlinePaymentsMinLimit: number;
      bonusGoodsEnabled: boolean;
      bonusServicesEnabled: boolean;
      isDepositRefillEnabled: boolean;
      isBonusPaymentTariffsEnabled: boolean;
      isUseDepositFirstEnabled: boolean;
      isUsagePercent: number;
      countryCode: string;
      cloudPaymentsVat: ENDS | null;
      cashierName: string;
      depositRefillTitle: string | null;
      lowStockEnabled: boolean;
      lowStockThreshold: number | '';
      isBonusSystemEnabled: boolean;
      kaspiOnlineEnabled: boolean;
      kaspiOnlineBin: string | null;
      clientRegister: boolean;
      registerWithoutVerification: boolean;
      stripeEnabled: boolean;
      stripeSecretKey: string;
      stripePublishableKey: string;
      shellSkinAccentColor: string;
      shellSkinAdditionalColor: string;
      shellSkinBackgroundColor: string;
      shellSkinLogo: string;
      tipsValues: number[];
      tipsEnabled: boolean;
      fastLoginAutostartSessionEnabled: boolean;
      fastLoginAutostartSessionTariffId: number | null;
      hostMap: string;
      hardwareChanges: boolean;
      lightShell: boolean;
      bePaidEnabled: boolean;
      bePaidSecretKey: string;
      bePaidPublishableKey: string;
      bePaidShopId: string;
    };
    isInitialized: boolean;
    isLoading: boolean;
  };
  disable: Set<ESettingsAliases>;
  actions: {
    loadSettings: () => Promise<unknown>;
    setSetting: TSetSetting;
    setSettings: TSetSettings;
  };
}

export interface ISetting {
  alias: ESettingsAliases;
  value: string;
  updated_at: string;
  disabled: boolean;
}

export interface IGetSettingsResponse {
  settingValues: Array<ISetting>;
  currencies: Array<ICurrency>;
}

export interface ISetSettingsValue {
  alias: ESettingsAliases;
  value: string | null;
}

export interface ISetSettingResponse {
  setSetting: Omit<ISetting, 'disabled'>;
}

export interface ISetSettingRequest {
  input: ISetSettingsValue;
}

export interface ISetSettingsResponse {
  setSettings: Array<Omit<ISetting, 'disabled'>>;
}

export interface ISetSettingsRequest {
  input: {
    list: Array<ISetSettingsValue>;
  };
}

export enum ECloudPaymentsActions {
  SET = 'SET',
  REMOVE = 'REMOVE',
}

export interface ISetCloudPaymentsRequest {
  input: {
    action: ECloudPaymentsActions;
    publicId: string;
    secretKey: string;
    vat: ENDS;
  };
}

export interface ISetCloudPaymentsResponse {
  setCloudPaymentsAccountToCompany: {
    publicId: string;
  };
}

export enum EOpf {
  OOO = 'OOO',
  OAO = 'OAO',
  AO = 'AO',
}

export interface IBeneficiary {
  balance: number;
  created_at: string;
  email: string;
  error: string;
  full_name: string;
  id: string;
  inn: string;
  opf: EOpf;
  organization_id: number;
  phone: string;
  status: string;
  type: string;
  locked_by: ILockedBy;
}

interface ILockedBy {
  beneficiary: IBeneficiary;
  commission: number;
  created_at: string;
  id: string;
  status: string;
  sum: number;
}

export interface IGetBeneficiariesResponse {
  getBeneficiaries: IBeneficiary[];
}

export interface IGetCurrentBeneficiaryResponse {
  getCurrentBeneficiary: IBeneficiary;
}

export type TTypeBeneficiary = 'UL_RESIDENT' | 'IP_RESIDENT';

export enum ECountry {
  RU = 'RU',
  KZ = 'KZ',
  UZ = 'UZ',
  BY = 'BY',
  GE = 'GE',
  AM = 'AM',
  KG = 'KG',
  CY = 'CY',
  ID = 'ID',
  BG = 'BG',
}

export enum EConnectPaymentService {
  TINKOFF = 'TINKOFF',
  STRIPE = 'STRIPE',
  BEPAID = 'BEPAID',
}

export interface IConnectPaymentServiceRequest {
  input: {
    bank_account?: {
      bank_account: string;
      bank_bik: string;
      bank_kpp?: string;
      bank_name: string;
      correspondent_account: string;
      service_name?: string;
      tax: Tax;
    };
    ip?: {
      birth_date: string;
      citizenship: ECountry;
      email?: string;
      first_name: string;
      inn: string;
      last_name: string;
      middle_name?: string;
      passport: {
        date: string;
        division_code: string;
        number: string;
        serial: string;
      };
      phone: string;
      registration_address: string;
      registration_date: string;
    };
    existing_beneficiary?: string;
    type?: TTypeBeneficiary;
    ul?: {
      email?: string;
      inn: string;
      legal_entity_address: string;
      name: string;
      ogrn?: string;
      opf: EOpf;
      phone: string;
      registration_date: string;
    };
    stripe_account?: {
      secret_key: string;
      publishable_key: string;
    };
    bepaid_account?: {
      shop_id: string;
      secret_key: string;
      publishable_key: string;
    };
  };
  service: EConnectPaymentService;
}

export interface IDisconnectPaymentServiceRequest {
  service: EConnectPaymentService;
}
